import React, { useEffect, useState, useCallback } from 'react';
import adminApi from './adminApi';
import { Karte, Chip, Leer, Fehler, datumZeit } from './bausteine';

const TabBetrieb = () => {
  const [runs, setRuns] = useState(null);
  const [konfig, setKonfig] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);
  const [laufend, setLaufend] = useState(null);

  const laden = useCallback(() => {
    adminApi.jobRuns().then(setRuns).catch((e) => setFehler(e.message));
    adminApi.templates().then(setTemplates).catch((e) => setTemplates({ fehler: e.message }));
    adminApi.konfigHealth().then(setKonfig).catch((e) => setKonfig({ fehler: e.message }));
  }, []);
  useEffect(laden, [laden]);

  const ausloesen = async (welcher) => {
    // force umgeht sowohl die Tagessperre als auch die Ortszeit-Pruefung –
    // damit gehen echte Nachrichten raus.
    const force = window.confirm(
      'Erzwingen?\n\nOK = auch ausserhalb der vorgesehenen Uhrzeit und trotz\n' +
      'bereits erfolgtem Lauf. Es gehen dann ECHTE Nachrichten raus.\n\n' +
      'Abbrechen = normaler Lauf (bricht ab, wenn nicht vorgesehen).'
    );
    setLaufend(welcher); setErgebnis(null);
    try {
      const r = welcher === 'daily'
        ? await adminApi.triggerDaily(force)
        : await adminApi.triggerEmergency(force);
      setErgebnis(JSON.stringify(r, null, 2));
      laden();
    } catch (e) {
      setFehler(e.response?.data?.error || e.message);
    } finally { setLaufend(null); }
  };

  return (
    <div className="space-y-6">
      <Fehler>{fehler}</Fehler>

      {/* Ganz oben: diese Luecken stehen sonst nur in den Lambda-Logs und
          bleiben deshalb monatelang offen. */}
      {konfig && !konfig.fehler && konfig.anzahl > 0 && (
        <Karte
          titel="Fehlende Konfiguration"
          rechts={<Chip farbe={konfig.hoch > 0 ? 'rot' : 'gelb'}>
            {konfig.anzahl} offen
          </Chip>}
        >
          <ul className="space-y-4">
            {konfig.befunde.map((b) => (
              <li key={b.schluessel} className="border-b border-gray-50 pb-3 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <Chip farbe={b.schwere === 'hoch' ? 'rot' : b.schwere === 'mittel' ? 'gelb' : 'grau'}>
                    {b.schwere}
                  </Chip>
                  <span className="font-medium">{b.titel}</span>
                  <span className="font-mono text-xs text-gray-400">{b.schluessel}</span>
                </div>
                <p className="text-sm text-gray-600">{b.folge}</p>
                <p className="text-sm text-gray-900 mt-1">{b.schritt}</p>
              </li>
            ))}
          </ul>
        </Karte>
      )}

      <Karte titel="WhatsApp-Vorlagen">
        {!templates ? <Leer>Lade …</Leer> : templates.fehler ? (
          <Fehler>{templates.fehler}</Fehler>
        ) : (
          <>
            {templates.probleme?.length > 0 && (
              <div className="mb-3"><Fehler>{templates.probleme.join(' · ')}</Fehler></div>
            )}
            <table className="w-full text-sm">
              <tbody>
                {templates.templates.map((t) => {
                  const genutzt = Object.values(templates.konfiguriert || {}).includes(t.name);
                  return (
                    <tr key={t.name + t.language} className="border-b border-gray-50">
                      <td className="py-2 font-mono text-xs">{t.name}</td>
                      <td className="py-2">
                        <Chip farbe={t.status === 'APPROVED' ? 'gruen'
                          : t.status === 'PENDING' ? 'gelb' : 'rot'}>{t.status}</Chip>
                      </td>
                      <td className="py-2 text-xs text-gray-500">{t.parameterFormat}</td>
                      <td className="py-2">
                        {genutzt ? <Chip farbe="blau">in Verwendung</Chip>
                          : <span className="text-xs text-gray-400">nicht konfiguriert</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Karte>

      <Karte titel="Jobs manuell auslösen">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => ausloesen('daily')} disabled={laufend}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
            {laufend === 'daily' ? 'läuft …' : 'Tagesmeldung'}
          </button>
          <button onClick={() => ausloesen('emergency')} disabled={laufend}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50">
            {laufend === 'emergency' ? 'läuft …' : 'Alarmierung'}
          </button>
        </div>
        {ergebnis && (
          <pre className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-x-auto">
            {ergebnis}
          </pre>
        )}
      </Karte>

      <Karte titel="Letzte Läufe">
        {!runs ? <Leer>Lade …</Leer> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4 font-medium">Lauf</th>
                  <th className="py-2 pr-4 font-medium">Start</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">Ergebnis</th>
                </tr>
              </thead>
              <tbody>
                {runs.runs.map((r) => (
                  <tr key={r.key} className="border-b border-gray-50">
                    <td className="py-2 pr-4 font-mono text-xs">{r.key}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">{datumZeit(r.startedAt)}</td>
                    <td className="py-2 pr-4">
                      <Chip farbe={r.status === 'completed' ? 'gruen'
                        : r.status === 'failed' ? 'rot' : 'gelb'}>{r.status}</Chip>
                    </td>
                    <td className="py-2 text-xs text-gray-600">
                      {r.result ? Object.entries(r.result)
                        .filter(([k]) => k !== 'failures' && k !== 'bilanz')
                        .map(([k, v]) => `${k}: ${v}`).join(' · ') : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
};

export default TabBetrieb;
